import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import {
    PlusCircle, Edit, Trash2, Loader2, AlertCircle, Info, ShieldAlert,
    CalendarDays, Sparkles, Search, X, Wand2, CheckCircle2, Clock
} from 'lucide-react';

const normalizeMissingPolicy = (val) => {
    if (val === 1 || val === '1' || val === 'AutoAssignFromWeekStart' || val === 'AutoAssignHolidayOvertime') {
        return 'AutoAssignFromWeekStart';
    }
    return 'WarningOnly';
};

// ====================================================================
//     Çalışma Periyodu Formu Bileşeni (Geniş ve Hızlı Şablonlu Modal)
// ====================================================================
const WorkPeriodForm = ({ currentPeriod, onSave, onCancel, isSubmitting, apiError }) => {
    const buildInitialState = (p) => p ? {
        ...p,
        missingDayOffPolicy: normalizeMissingPolicy(p.missingDayOffPolicy),
        dynamicDayOffCount: p.dynamicDayOffCount || 1
    } : {
        name: '',
        mondayShiftGroupId: null,
        tuesdayShiftGroupId: null,
        wednesdayShiftGroupId: null,
        thursdayShiftGroupId: null,
        fridayShiftGroupId: null,
        saturdayShiftGroupId: null,
        sundayShiftGroupId: null,
        hasDynamicDayOff: false,
        dynamicDayOffCount: 1,
        missingDayOffPolicy: 'WarningOnly'
    };

    const [formData, setFormData] = useState(() => buildInitialState(currentPeriod));
    const [availableGroups, setAvailableGroups] = useState([]);
    const [quickGroupId, setQuickGroupId] = useState('');
    const [validationError, setValidationError] = useState(null);

    useEffect(() => {
        setFormData(buildInitialState(currentPeriod));
    }, [currentPeriod]);

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const data = await apiClient('scheduling/groups', { method: 'GET' });
                setAvailableGroups(data || []);
                if (data && data.length > 0) {
                    setQuickGroupId(data[0].id.toString());
                }
            } catch {
                // sessizce geç
            }
        };
        fetchGroups();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = type === 'checkbox' ? checked : value;
        if (name.includes('ShiftGroupId') && finalValue === '') {
            finalValue = null;
        }
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    const applyQuickTemplate = (mode) => {
        if (!quickGroupId) return;
        const gId = parseInt(quickGroupId, 10);
        if (mode === '5_2') {
            setFormData(prev => ({
                ...prev,
                mondayShiftGroupId: gId,
                tuesdayShiftGroupId: gId,
                wednesdayShiftGroupId: gId,
                thursdayShiftGroupId: gId,
                fridayShiftGroupId: gId,
                saturdayShiftGroupId: null,
                sundayShiftGroupId: null,
                hasDynamicDayOff: false
            }));
        } else if (mode === '6_1') {
            setFormData(prev => ({
                ...prev,
                mondayShiftGroupId: gId,
                tuesdayShiftGroupId: gId,
                wednesdayShiftGroupId: gId,
                thursdayShiftGroupId: gId,
                fridayShiftGroupId: gId,
                saturdayShiftGroupId: gId,
                sundayShiftGroupId: null,
                hasDynamicDayOff: false
            }));
        } else if (mode === '7_DYNAMIC') {
            setFormData(prev => ({
                ...prev,
                mondayShiftGroupId: gId,
                tuesdayShiftGroupId: gId,
                wednesdayShiftGroupId: gId,
                thursdayShiftGroupId: gId,
                fridayShiftGroupId: gId,
                saturdayShiftGroupId: gId,
                sundayShiftGroupId: gId,
                hasDynamicDayOff: true
            }));
        }
    };

    const days = [
        { key: 'mondayShiftGroupId', name: 'Pazartesi', short: 'Pzt' },
        { key: 'tuesdayShiftGroupId', name: 'Salı', short: 'Sal' },
        { key: 'wednesdayShiftGroupId', name: 'Çarşamba', short: 'Çar' },
        { key: 'thursdayShiftGroupId', name: 'Perşembe', short: 'Per' },
        { key: 'fridayShiftGroupId', name: 'Cuma', short: 'Cum' },
        { key: 'saturdayShiftGroupId', name: 'Cumartesi', short: 'Cmt' },
        { key: 'sundayShiftGroupId', name: 'Pazar', short: 'Paz' },
    ];

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.hasDynamicDayOff) {
            const hasEmptyDay = days.some(day => !formData[day.key]);
            if (hasEmptyDay) {
                setValidationError("Dinamik Hafta Tatili aktifken hiçbir güne sabit 'Hafta Tatili' bırakılamaz. Lütfen haftanın 7 günü için de bir vardiya grubu seçin (Hızlı Şablon çubuğundan '7 Gün + Dinamik HT' butonuna tıklayabilirsiniz).");
                return;
            }
        }

        const payloadToSave = { ...formData };
        if (payloadToSave.hasDynamicDayOff) {
            payloadToSave.dynamicDayOffCount = parseInt(payloadToSave.dynamicDayOffCount || 1, 10);
            payloadToSave.missingDayOffPolicy = normalizeMissingPolicy(payloadToSave.missingDayOffPolicy);
        }

        onSave(payloadToSave);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl border border-gray-200 dark:border-gray-700 max-h-[92vh] flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                            <CalendarDays size={22} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {formData.id ? 'Çalışma Periyodunu Düzenle' : 'Yeni Çalışma Periyodu Oluştur'}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Haftalık 7 gün çalışma takvimini ve dinamik hafta tatili kurallarını yapılandırın.
                            </p>
                        </div>
                    </div>
                    <button onClick={onCancel} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-6">
                    {apiError && (
                        <div className="text-red-700 bg-red-50 dark:bg-red-900/30 p-3.5 rounded-xl border border-red-200 dark:border-red-800 text-sm flex items-center">
                            <AlertCircle size={18} className="mr-2 flex-shrink-0" /> {apiError}
                        </div>
                    )}

                    <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                            Çalışma Periyodu Adı
                        </label>
                        <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Örn: Beyaz Yaka 5+2 Ofis Planı, Mağaza Dinamik HT Planı"
                            className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                        />
                    </div>

                    {/* Hızlı Haftalık Şablon Doldurucu */}
                    {availableGroups.length > 0 && (
                        <div className="p-4 rounded-xl bg-indigo-50/70 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5">
                                <Wand2 size={18} className="text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                                <div>
                                    <span className="text-xs font-bold text-indigo-950 dark:text-indigo-200 block">Hızlı Haftalık Şablon Doldurucu</span>
                                    <span className="text-[11px] text-indigo-700 dark:text-indigo-300">Seçtiğiniz grubu tek tıkla günlere dağıtın:</span>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <select
                                    value={quickGroupId}
                                    onChange={(e) => setQuickGroupId(e.target.value)}
                                    className="px-3 py-1.5 text-xs font-semibold border border-indigo-300 dark:border-indigo-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                >
                                    {availableGroups.map(g => (
                                        <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => applyQuickTemplate('5_2')}
                                    className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white dark:bg-gray-800 border border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-600 hover:text-white transition-colors"
                                >
                                    Pzt-Cum Ata (Cmt-Paz HT)
                                </button>
                                <button
                                    type="button"
                                    onClick={() => applyQuickTemplate('6_1')}
                                    className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white dark:bg-gray-800 border border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-600 hover:text-white transition-colors"
                                >
                                    Pzt-Cmt Ata (Paz HT)
                                </button>
                                <button
                                    type="button"
                                    onClick={() => applyQuickTemplate('7_DYNAMIC')}
                                    className="px-3 py-1.5 text-xs font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                                >
                                    7 Güne Ata (Dinamik HT)
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 7 Günlük Vardiya Grubu Seçimi */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                            Haftalık Gün Bazlı Vardiya Grubu Ataması
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {days.map(day => {
                                const val = formData[day.key];
                                const isWorking = !!val;
                                return (
                                    <div
                                        key={day.key}
                                        className={`p-3 rounded-xl border transition-all ${
                                            isWorking
                                                ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-900/10'
                                                : 'border-amber-300 dark:border-amber-800/70 bg-amber-50/40 dark:bg-amber-900/15'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <label htmlFor={day.key} className="text-xs font-bold text-gray-800 dark:text-gray-200">
                                                {day.name}
                                            </label>
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                                isWorking
                                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                                                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
                                            }`}>
                                                {isWorking ? 'Mesai Günü' : 'Hafta Tatili'}
                                            </span>
                                        </div>
                                        <select
                                            name={day.key}
                                            id={day.key}
                                            value={val || ''}
                                            onChange={handleChange}
                                            className="w-full px-2.5 py-2 text-xs font-semibold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        >
                                            <option value="">🏖️ Hafta Tatili (HT)</option>
                                            {availableGroups.map(group => (
                                                <option key={group.id} value={group.id}>💼 {group.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Dinamik Hafta Tatili Bölümü */}
                    <div className="p-4 rounded-xl bg-blue-50/60 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 space-y-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                id="hasDynamicDayOff"
                                name="hasDynamicDayOff"
                                type="checkbox"
                                checked={formData.hasDynamicDayOff}
                                onChange={handleChange}
                                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <div className="text-sm">
                                <div className="font-bold text-blue-950 dark:text-blue-200">Dinamik Hafta Tatili (Esnek İzin Günü) Aktif Et</div>
                                <p className="text-xs text-blue-800/80 dark:text-blue-300/80 mt-0.5">
                                    Personelin hafta tatili günü sabit değilse (Örn: AVM, mağaza, 7/24 vardiya), haftanın 7 gününe de vardiya grubu atayın ve bu seçeneği açın. Sistem personelin gelmediği günü otomatik olarak Devamsızlıktan çıkarıp Hafta Tatili (HT) yapar.
                                </p>
                            </div>
                        </label>

                        {formData.hasDynamicDayOff && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-blue-200/70 dark:border-blue-800/60">
                                <div>
                                    <label htmlFor="dynamicDayOffCount" className="block text-xs font-bold uppercase text-blue-900 dark:text-blue-200 mb-1">
                                        Haftalık Dinamik Tatil Hakkı (Gün)
                                    </label>
                                    <input
                                        type="number"
                                        name="dynamicDayOffCount"
                                        id="dynamicDayOffCount"
                                        value={formData.dynamicDayOffCount || 1}
                                        onChange={handleChange}
                                        min="1"
                                        max="6"
                                        className="w-full px-3.5 py-2 border border-blue-300 dark:border-blue-700 rounded-xl bg-white dark:bg-gray-800 text-sm font-bold"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="missingDayOffPolicy" className="block text-xs font-bold uppercase text-blue-900 dark:text-blue-200 mb-1">
                                        Personel 7 Günün Tamamında Gelirse (Eksik HT Politikası)
                                    </label>
                                    <select
                                        name="missingDayOffPolicy"
                                        id="missingDayOffPolicy"
                                        value={formData.missingDayOffPolicy}
                                        onChange={handleChange}
                                        className="w-full px-3.5 py-2 border border-blue-300 dark:border-blue-700 rounded-xl bg-white dark:bg-gray-800 text-sm font-semibold"
                                    >
                                        <option value="WarningOnly">Sadece Puantaj Kalite Kontrolde Uyarı Ver</option>
                                        <option value="AutoAssignFromWeekStart">Otomatik HT Ata ve Çalışmayı HT Fazla Mesaisi (HT FM) Say</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                            className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 font-medium text-sm"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm flex items-center shadow-lg shadow-emerald-500/25"
                        >
                            {isSubmitting && <Loader2 className="animate-spin mr-2" size={18} />}
                            {formData.id ? 'Periyodu Güncelle' : 'Çalışma Periyodunu Kaydet'}
                        </button>
                    </div>
                </form>
            </div>

            {validationError && (
                <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[60] p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-xl border border-red-200 dark:border-red-900/50">
                        <div className="flex items-center text-red-600 dark:text-red-400 mb-3">
                            <ShieldAlert size={26} className="mr-2.5 flex-shrink-0" />
                            <h3 className="text-lg font-bold">Dinamik Hafta Tatili Kuralı</h3>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 text-sm mb-5 leading-relaxed">
                            {validationError}
                        </p>
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => setValidationError(null)}
                                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-xl text-white font-semibold text-sm"
                            >
                                Anladım
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const WEEK_DAYS_SUMMARY = [
    { key: 'mondayShiftGroup', label: 'Pzt' },
    { key: 'tuesdayShiftGroup', label: 'Sal' },
    { key: 'wednesdayShiftGroup', label: 'Çar' },
    { key: 'thursdayShiftGroup', label: 'Per' },
    { key: 'fridayShiftGroup', label: 'Cum' },
    { key: 'saturdayShiftGroup', label: 'Cmt' },
    { key: 'sundayShiftGroup', label: 'Paz' }
];

// ====================================================================
//     Ana Çalışma Periyodu Yönetim Bileşeni
// ====================================================================
export default function WorkPeriodManagement({ onDataChange }) {
    const { isAuthenticated, permissions } = useAuth();
    const [workPeriods, setWorkPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPeriod, setCurrentPeriod] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deleteConfirmData, setDeleteConfirmData] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const canView = useMemo(() => permissions.includes('work_periods:view_list'), [permissions]);
    const canCreate = useMemo(() => permissions.includes('work_periods:create'), [permissions]);
    const canUpdate = useMemo(() => permissions.includes('work_periods:update'), [permissions]);
    const canDelete = useMemo(() => permissions.includes('work_periods:delete'), [permissions]);

    const fetchWorkPeriods = useCallback(async () => {
        if (!canView) {
            setError("Çalışma periyotlarını görüntüleme yetkiniz yok.");
            setLoading(false);
            return;
        }
        if (!isAuthenticated) return;
        setLoading(true);
        setError(null);
        try {
            const data = await apiClient('scheduling/periods', { method: 'GET' });
            setWorkPeriods(data || []);
            if (onDataChange) onDataChange();
        } catch (err) {
            setError(err.message || 'Çalışma periyotları yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, canView, onDataChange]);

    useEffect(() => {
        fetchWorkPeriods();
    }, [fetchWorkPeriods]);

    const handleOpenModal = (period = null) => {
        const requiredPermission = period ? 'work_periods:update' : 'work_periods:create';
        if (!permissions.includes(requiredPermission)) {
            alert("Bu işlemi yapma yetkiniz bulunmamaktadır.");
            return;
        }

        const periodData = period ? {
            ...period,
            missingDayOffPolicy: normalizeMissingPolicy(period.missingDayOffPolicy),
            mondayShiftGroupId: period.mondayShiftGroup?.id || null,
            tuesdayShiftGroupId: period.tuesdayShiftGroup?.id || null,
            wednesdayShiftGroupId: period.wednesdayShiftGroup?.id || null,
            thursdayShiftGroupId: period.thursdayShiftGroup?.id || null,
            fridayShiftGroupId: period.fridayShiftGroup?.id || null,
            saturdayShiftGroupId: period.saturdayShiftGroup?.id || null,
            sundayShiftGroupId: period.sundayShiftGroup?.id || null,
        } : null;
        setCurrentPeriod(periodData);
        setError(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    const handleSave = async (formData) => {
        setIsSubmitting(true);
        setError(null);
        try {
            const method = formData.id ? 'PUT' : 'POST';
            const url = formData.id ? `scheduling/periods/${formData.id}` : 'scheduling/periods';

            const payload = {
                name: formData.name,
                hasDynamicDayOff: formData.hasDynamicDayOff,
                dynamicDayOffCount: formData.hasDynamicDayOff ? (formData.dynamicDayOffCount ? parseInt(formData.dynamicDayOffCount, 10) : 1) : null,
                missingDayOffPolicy: formData.hasDynamicDayOff ? normalizeMissingPolicy(formData.missingDayOffPolicy) : 'WarningOnly',
                mondayShiftGroupId: formData.mondayShiftGroupId ? parseInt(formData.mondayShiftGroupId, 10) : null,
                tuesdayShiftGroupId: formData.tuesdayShiftGroupId ? parseInt(formData.tuesdayShiftGroupId, 10) : null,
                wednesdayShiftGroupId: formData.wednesdayShiftGroupId ? parseInt(formData.wednesdayShiftGroupId, 10) : null,
                thursdayShiftGroupId: formData.thursdayShiftGroupId ? parseInt(formData.thursdayShiftGroupId, 10) : null,
                fridayShiftGroupId: formData.fridayShiftGroupId ? parseInt(formData.fridayShiftGroupId, 10) : null,
                saturdayShiftGroupId: formData.saturdayShiftGroupId ? parseInt(formData.saturdayShiftGroupId, 10) : null,
                sundayShiftGroupId: formData.sundayShiftGroupId ? parseInt(formData.sundayShiftGroupId, 10) : null,
            };

            if (formData.id) payload.id = formData.id;

            await apiClient(url, { method, body: payload });
            handleCloseModal();
            fetchWorkPeriods();
        } catch (err) {
            setError(err.message || 'Bir hata oluştu.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        if (!canDelete) {
            setDeleteConfirmData({
                type: 'block',
                message: "Bu işlemi yapma yetkiniz bulunmamaktadır."
            });
            return;
        }
        setDeleteConfirmData({
            id,
            type: 'initial',
            message: "Bu çalışma periyodunu silmek istediğinizden emin misiniz?"
        });
    };

    const handleDeleteConfirm = async (id) => {
        try {
            await apiClient(`scheduling/periods/${id}`, { method: 'DELETE' });
            setDeleteConfirmData(null);
            fetchWorkPeriods();
        } catch (err) {
            setDeleteConfirmData({
                type: 'block',
                message: err.payload?.message || err.message || 'Silme işlemi sırasında bir hata oluştu.'
            });
        }
    };

    const filteredPeriods = useMemo(() => {
        return workPeriods.filter(p =>
            !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [workPeriods, searchQuery]);

    const stats = useMemo(() => ({
        total: workPeriods.length,
        fixed: workPeriods.filter(p => !p.hasDynamicDayOff).length,
        dynamic: workPeriods.filter(p => p.hasDynamicDayOff).length
    }), [workPeriods]);

    if (!loading && !canView) {
        return (
            <div className="p-8 bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-200 dark:border-red-800 text-center">
                <ShieldAlert className="mx-auto h-12 w-12 text-red-400" />
                <h3 className="mt-2 text-lg font-bold text-red-800 dark:text-red-300">Yetki Hatası</h3>
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* KPI Özet Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Toplam Çalışma Periyodu</p>
                        <p className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">{stats.total}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                        <CalendarDays size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Sabit Haftalık Planlar</p>
                        <p className="text-2xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-1">{stats.fixed}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                        <CheckCircle2 size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Dinamik Hafta Tatilli</p>
                        <p className="text-2xl font-extrabold text-blue-600 dark:text-blue-400 mt-1">{stats.dynamic}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                        <Sparkles size={22} />
                    </div>
                </div>
            </div>

            {/* Ana Liste Kartı */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <CalendarDays size={20} className="text-emerald-500" />
                            Çalışma Periyotları (7 Günlük Haftalık Takvim Şablonları)
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Personellere atanacak haftalık çalışma ve hafta tatili (HT) düzenlerini yönetin.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Periyot adı ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                        {canCreate && (
                            <button
                                onClick={() => handleOpenModal()}
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl flex items-center shadow-sm shadow-emerald-500/20 transition-colors"
                            >
                                <PlusCircle size={17} className="mr-1.5" />
                                Yeni Periyot Ekle
                            </button>
                        )}
                    </div>
                </div>

                {loading && (
                    <div className="p-12 flex justify-center items-center text-gray-500">
                        <Loader2 className="animate-spin mr-2" size={20} /> Çalışma periyotları yükleniyor...
                    </div>
                )}

                {error && !isModalOpen && (
                    <div className="m-5 text-red-600 bg-red-50 dark:bg-red-900/20 p-4 rounded-xl flex items-center text-sm">
                        <AlertCircle size={18} className="mr-2" /> {error}
                    </div>
                )}

                {!loading && !error && (
                    <div className="p-5 space-y-4">
                        {filteredPeriods.map(period => {
                            const isAutoPolicy = normalizeMissingPolicy(period.missingDayOffPolicy) === 'AutoAssignFromWeekStart';
                            const workingDaysCount = WEEK_DAYS_SUMMARY.filter(d => !!period[d.key]).length;
                            const offDaysCount = period.hasDynamicDayOff ? (period.dynamicDayOffCount || 1) : (7 - workingDaysCount);

                            return (
                                <div
                                    key={period.id}
                                    className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-gray-50/40 dark:bg-gray-900/30 hover:border-emerald-300 dark:hover:border-emerald-700 transition-all"
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                                        <div className="flex flex-wrap items-center gap-2.5">
                                            <span className="font-bold text-base text-gray-900 dark:text-white">{period.name}</span>
                                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">
                                                {7 - offDaysCount} Gün Mesai • {offDaysCount} Gün HT
                                            </span>
                                            {period.hasDynamicDayOff ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                                    Dinamik HT: Haftada {period.dynamicDayOffCount} Gün | Eksik HT: {isAutoPolicy ? 'Oto HT FM Ver' : 'Sadece Uyarı'}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                                    Sabit Haftalık Takvim
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-1">
                                            {canUpdate && (
                                                <button
                                                    onClick={() => handleOpenModal(period)}
                                                    className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                                                    title="Düzenle"
                                                >
                                                    <Edit size={17} />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() => handleDeleteClick(period.id)}
                                                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                                                    title="Sil"
                                                >
                                                    <Trash2 size={17} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* 7 Günlük Haftalık Plan Özeti */}
                                    <div className="grid grid-cols-2 sm:grid-cols-7 gap-2 pt-2 border-t border-gray-200/70 dark:border-gray-700/60">
                                        {WEEK_DAYS_SUMMARY.map(({ key, label }) => {
                                            const sg = period[key];
                                            return (
                                                <div
                                                    key={key}
                                                    className={`rounded-lg px-2.5 py-2 text-xs border ${
                                                        sg
                                                            ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 shadow-sm'
                                                            : 'bg-amber-50/80 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-300'
                                                    }`}
                                                >
                                                    <div className="font-extrabold text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{label}</div>
                                                    {sg ? (
                                                        <div className="truncate font-semibold" title={sg.name}>
                                                            {sg.name}
                                                        </div>
                                                    ) : (
                                                        <div className="font-bold">Hafta Tatili</div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                        {filteredPeriods.length === 0 && (
                            <div className="text-center py-10 text-gray-500 text-sm">
                                Kriterlere uygun çalışma periyodu bulunamadı.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {isModalOpen && <WorkPeriodForm currentPeriod={currentPeriod} onSave={handleSave} onCancel={handleCloseModal} isSubmitting={isSubmitting} apiError={error} />}

            {/* Dinamik Silme Modalı */}
            {deleteConfirmData && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[60] p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0 bg-red-100 dark:bg-red-900/30 p-3 rounded-full mr-4">
                                {deleteConfirmData.type === 'block' ? (
                                    <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400" />
                                ) : (
                                    <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                                )}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                                    {deleteConfirmData.type === 'block' ? 'Silme İşlemi Engellendi' : 'Silme Onayı'}
                                </h3>
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                    {deleteConfirmData.message}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => setDeleteConfirmData(null)}
                                className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium text-sm"
                            >
                                {deleteConfirmData.type === 'block' ? 'Kapat' : 'İptal'}
                            </button>
                            {deleteConfirmData.type !== 'block' && (
                                <button
                                    onClick={() => handleDeleteConfirm(deleteConfirmData.id)}
                                    className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium text-sm flex items-center shadow-lg shadow-red-500/30"
                                >
                                    Evet, Sil
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
